import React, { useState, useEffect } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { apiFetch } from "../../utils/api";
import { toast } from "react-hot-toast";
import { MdTrendingUp, MdAttachMoney, MdErrorOutline, MdSpeed, MdPieChart, MdEdit, MdCheck, MdClose } from "react-icons/md";

const COLORS = ["#d63384", "#6610f2", "#0d6efd", "#20c997", "#ffc107"];

const SEOAdminDashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateRange, setDateRange] = useState({ 
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  // Budget Management State
  const [limit, setLimit] = useState(100.0);
  const [isEditing, setIsEditing] = useState(false);
  const [newLimit, setNewLimit] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Auth State (Simple localStorage check)
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperAdmin = user.role?.toUpperCase() === "SUPERADMIN";

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/seo/metrics?from=${dateRange.from}&to=${dateRange.to}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setMetrics(data);
      setLimit(data.monthlyLimit || 100.0);
      setNewLimit(data.monthlyLimit || 100.0);
    } catch (err) {
      setError("Failed to fetch metrics: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLimit = async () => {
    if (!newLimit || isNaN(newLimit)) return toast.error("Please enter a valid number");
    setIsSaving(true);
    try {
      const res = await apiFetch("/seo/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ monthlyLimit: Number(newLimit) })
      });
      if (!res.ok) throw new Error("Failed to update limit");
      setLimit(Number(newLimit));
      setIsEditing(false);
      // Refresh metrics to update progress bar
      fetchMetrics();
    } catch (err) {
      toast.error("Error saving limit: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [dateRange]);

  if (loading) return <div className="p-5 text-center text-muted">Loading Console...</div>;
  if (error) return <div className="p-5 text-danger">{error}</div>;

  const currentSpend = metrics.summary.totalCost || 0;
  const progressPercent = Math.min((currentSpend / limit) * 100, 100);
  const progressClass = progressPercent > 80 ? "bg-danger" : progressPercent > 50 ? "bg-warning" : "bg-info";

  return (
    <div className="seo-admin-dashboard p-4">
      {/* HEADER & GOVERNANCE */}
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-4 mb-5">
        <div>
          <h2 className="m-0 fw-bold">Financial Observability</h2>
          <small className="text-muted">Track real-time token expenditure and model attribution</small>
        </div>
        
        <div className="governance-card p-3 rounded border shadow-sm bg-white" style={{ minWidth: 320 }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small text-muted">MONTHLY SPEND</span>
            <div className="d-flex align-items-center gap-2">
              {isEditing ? (
                <div className="d-flex align-items-center gap-1">
                  <input 
                    type="number" 
                    className="form-control form-control-sm" 
                    style={{ width: 80 }}
                    value={newLimit}
                    onChange={(e) => setNewLimit(e.target.value)}
                    autoFocus
                  />
                  <button className="btn btn-sm btn-success p-1" onClick={handleSaveLimit} disabled={isSaving}>
                    <MdCheck size={16}/>
                  </button>
                  <button className="btn btn-sm btn-light p-1" onClick={() => setIsEditing(false)}>
                    <MdClose size={16}/>
                  </button>
                </div>
              ) : (
                <div className="d-flex align-items-center gap-2">
                  <span className="small fw-bold">${currentSpend.toFixed(2)} / ${limit}</span>
                  {isSuperAdmin && (
                    <button className="btn btn-link p-0 text-muted" onClick={() => setIsEditing(true)}>
                      <MdEdit size={16} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="progress" style={{ height: 6 }}>
            <div 
              className={`progress-bar ${progressClass} progress-bar-animated progress-bar-striped`} 
              role="progressbar" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="d-flex justify-content-between mt-2">
            <small className="text-muted">{progressPercent.toFixed(1)}% used</small>
            <input 
              type="date" 
              className="console-date-input-light"
              value={dateRange.to}
              onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
            />
          </div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="row g-3 mb-5">
        {[
          { label: "Total Spend", value: `$${metrics.summary.totalCost.toFixed(2)}`, icon: <MdAttachMoney size={22}/>, color: "#d63384", bg: "#fff" },
          { label: "Total Requests", value: metrics.summary.totalRequests, icon: <MdTrendingUp size={22}/>, color: "#6610f2", bg: "#fff" },
          { label: "Avg Cost/Req", value: `$${(metrics.summary.totalCost / (metrics.summary.totalRequests || 1)).toFixed(3)}`, icon: <MdSpeed size={22}/>, color: "#0d6efd", bg: "#fff" },
          { label: "Error Rate", value: `${(metrics.errorRate * 100).toFixed(1)}%`, icon: <MdErrorOutline size={22}/>, color: "#dc3545", bg: "#fff" },
        ].map((kpi, i) => (
          <div key={i} className="col-md-3">
            <div className="kpi-card h-100 p-3 rounded border-0 shadow-sm bg-white">
              <div className="icon-circle mb-2" style={{ backgroundColor: `${kpi.color}15`, color: kpi.color }}>
                {kpi.icon}
              </div>
              <div className="small text-muted mb-1">{kpi.label}</div>
              <h3 className="m-0 fw-bold">{kpi.value}</h3>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mb-5">
        {/* DAILY COST BAR CHART */}
        <div className="col-md-8">
          <div className="card h-100 border-0 shadow-sm p-4 rounded bg-white">
            <h5 className="mb-4 d-flex align-items-center gap-2">
              <span className="dot bg-primary"></span> Daily Token Cost (USD)
            </h5>
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={metrics.dailyUsage}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis dataKey="_id" stroke="#999" fontSize={12} tickFormatter={(v) => v.split('-').slice(1).join('/')} />
                  <YAxis stroke="#999" fontSize={12} tickFormatter={(v) => `$${v}`} />
                  <Tooltip 
                    contentStyle={{ border: "1px solid #ddd", borderRadius: 8 }}
                  />
                  <Bar dataKey="cost" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* COST BY MODEL DONUT */}
        <div className="col-md-4">
          <div className="card h-100 border-0 shadow-sm p-4 rounded bg-white">
            <h5 className="mb-4 d-flex align-items-center gap-2">
              <MdPieChart className="text-info" /> Cost by Model
            </h5>
            <div style={{ width: "100%", height: 320, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={metrics.costByModel}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="cost"
                    nameKey="_id"
                  >
                    {metrics.costByModel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 w-100 overflow-auto custom-scrollbar" style={{ maxHeight: 100 }}>
                {metrics.costByModel.map((m, i) => (
                  <div key={i} className="d-flex justify-content-between align-items-center mb-1 small">
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <span className="dot-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                      {m._id}
                    </div>
                    <span className="fw-bold">${m.cost.toFixed(3)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* TOP USERS TABLE */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm p-4 rounded bg-white">
            <h5 className="mb-4">Top Collectors Attribution</h5>
            <div className="console-table-container">
              {metrics.topUsers.map((u, idx) => (
                <div key={idx} className="d-flex align-items-center justify-content-between py-3 border-bottom">
                  <div className="d-flex align-items-center gap-3">
                    <div className="user-rank-light">{idx + 1}</div>
                    <div>
                      <div className="fw-bold">{u.email}</div>
                      <small className="text-muted">{u.count} total audits</small>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="text-primary fw-bold">${u.cost.toFixed(3)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* TOP URLS COST TABLE */}
        <div className="col-md-6">
          <div className="card border-0 shadow-sm p-4 rounded bg-white">
            <h5 className="mb-4">Most Expensive URLs</h5>
            <div className="console-table-container">
              {metrics.topUrls.map((u, idx) => (
                <div key={idx} className="d-flex align-items-center justify-content-between py-3 border-bottom">
                  <div className="text-truncate me-3" style={{ maxWidth: 300 }}>
                    <div className="text-muted small mb-1">{u._id}</div>
                    <div className="fw-bold">{u.count} Audits</div>
                  </div>
                  <div className="text-end">
                    <div className="text-info fw-bold">${u.cost.toFixed(3)}</div>
                  </div>
                </div>
              ))}
              {metrics.topUrls.length === 0 && (
                <div className="text-center py-5 text-muted">No URL attribution data</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

export default SEOAdminDashboard;
